/**
 * 2D SVG blueprint renderer for a fabrication build.
 * Reads the `spec` object returned by /api/fabrication/calculate and draws a
 * clean, dimensioned elevation/side-profile. Deterministic — no external deps.
 */
const W = 460, H = 300;
const STROKE = "#334155";      // slate-700
const ACCENT = "#0891b2";      // cyan-600
const MUTED = "#94a3b8";       // slate-400
const GLASS = "rgba(6,182,212,0.16)";

function Wrap({ children }) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" data-testid="fab-blueprint"
      style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
      {/* faint grid */}
      <defs>
        <pattern id="fabgrid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0 L0 0 0 20" fill="none" stroke="#eef2f6" strokeWidth="1" />
        </pattern>
      </defs>
      <rect x="0" y="0" width={W} height={H} fill="url(#fabgrid)" />
      {children}
    </svg>
  );
}

function Staircase({ spec }) {
  const rise = spec.total_rise_mm || 0;
  const run = spec.total_run_mm || 0;
  const nR = spec.num_risers || 0;
  const nT = spec.num_treads || 0;
  const riser = spec.actual_riser_mm || (nR ? rise / nR : 0);
  const going = spec.tread_depth_mm || (nT ? run / nT : 0);
  const padL = 55, padR = 30, padT = 30, padB = 40;
  const drawW = W - padL - padR, drawH = H - padT - padB;
  const sc = Math.min(run ? drawW / run : drawW, rise ? drawH / rise : drawH) || 0.05;
  const ox = padL, oy = H - padB;
  let x = ox, y = oy;
  const pts = [[x, y]];
  for (let i = 0; i < nR; i++) {
    y -= riser * sc; pts.push([x, y]);
    if (i < nR - 1) { x += going * sc; pts.push([x, y]); }
  }
  const pathStr = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  return (
    <Wrap>
      <line x1={padL - 12} y1={oy} x2={W - padR} y2={oy} stroke={MUTED} strokeWidth="1.5" />
      <line x1={ox} y1={oy} x2={x} y2={y} stroke={ACCENT} strokeWidth="2" strokeDasharray="5 3" />
      <polyline points={pathStr} fill="none" stroke={STROKE} strokeWidth="2.5" strokeLinejoin="miter" />
      <text x={padL - 16} y={(oy + y) / 2} textAnchor="middle" fontSize="10" fill="#475569" transform={`rotate(-90 ${padL - 16} ${(oy + y) / 2})`}>Rise {rise}mm</text>
      <text x={(ox + x) / 2} y={oy + 18} textAnchor="middle" fontSize="10" fill="#475569">Run {run}mm · {nT} treads</text>
      {nR > 0 && (
        <>
          <text x={ox + going * sc + 5} y={oy - riser * sc / 2 + 3} fontSize="9" fill={ACCENT}>R {riser.toFixed(0)}</text>
          <text x={ox + going * sc / 2} y={oy - riser * sc - 5} textAnchor="middle" fontSize="9" fill={ACCENT}>G {going.toFixed(0)}</text>
        </>
      )}
    </Wrap>
  );
}

function Balustrade({ spec }) {
  const nP = spec.num_posts || 0;
  const infill = spec.infill_type;
  const nBal = spec.num_balusters || 0;
  const nPan = spec.num_panels || 0;
  const padL = 50, padR = 30;
  const oxL = padL, oxR = W - padR;
  const topY = 55, botY = 245, hrY = topY - 14;
  const span = oxR - oxL;
  const postXs = [];
  for (let i = 0; i < nP; i++) postXs.push(oxL + (nP > 1 ? (span * i) / (nP - 1) : 0));
  const bars = [];
  const drawn = Math.min(nBal, 70);
  if (infill === "vertical_bar" && drawn > 0) {
    for (let i = 1; i <= drawn; i++) bars.push(oxL + (span * i) / (drawn + 1));
  }
  const panels = [];
  if (infill === "glass" && nPan > 0) {
    const pw = span / nPan;
    for (let i = 0; i < nPan; i++) panels.push([oxL + i * pw + 3, pw - 6]);
  }
  return (
    <Wrap>
      <line x1={oxL - 10} y1={botY} x2={oxR + 6} y2={botY} stroke={MUTED} strokeWidth="1.5" />
      {/* infill */}
      {infill === "glass" && panels.map(([px, pw], i) => (
        <rect key={`g${i}`} x={px} y={topY + 4} width={pw} height={botY - topY - 8} fill={GLASS} stroke={ACCENT} strokeWidth="1" />
      ))}
      {infill === "vertical_bar" && bars.map((bx, i) => (
        <line key={`b${i}`} x1={bx} y1={topY} x2={bx} y2={botY} stroke={STROKE} strokeWidth="1" />
      ))}
      {infill === "wire" && [0, 1, 2, 3, 4].map((i) => (
        <line key={`w${i}`} x1={oxL} y1={topY + ((botY - topY) * (i + 1)) / 6} x2={oxR} y2={topY + ((botY - topY) * (i + 1)) / 6} stroke={STROKE} strokeWidth="1" strokeDasharray="2 3" />
      ))}
      {/* rails */}
      <line x1={oxL} y1={topY} x2={oxR} y2={topY} stroke={STROKE} strokeWidth="3" />
      <line x1={oxL} y1={botY} x2={oxR} y2={botY} stroke={STROKE} strokeWidth="3" />
      {spec.has_handrail && <line x1={oxL} y1={hrY} x2={oxR} y2={hrY} stroke={ACCENT} strokeWidth="3" />}
      {/* posts */}
      {postXs.map((px, i) => (
        <line key={`p${i}`} x1={px} y1={hrY - 4} x2={px} y2={botY} stroke={STROKE} strokeWidth="4" />
      ))}
      <text x={padL - 14} y={(topY + botY) / 2} textAnchor="middle" fontSize="10" fill="#475569" transform={`rotate(-90 ${padL - 14} ${(topY + botY) / 2})`}>{spec.height_mm}mm</text>
      <text x={(oxL + oxR) / 2} y={botY + 20} textAnchor="middle" fontSize="10" fill="#475569">{spec.length_m}m · {nP} posts{infill === "vertical_bar" ? ` · ${nBal} bars` : infill === "glass" ? ` · ${nPan} panels` : ""}</text>
    </Wrap>
  );
}

function Frame({ spec }) {
  const w = spec.frame_width_mm || 1, h = spec.frame_height_mm || 1;
  const nV = spec.num_verticals || 0, nH = spec.num_horizontals || 0;
  const padL = 55, padR = 35, padT = 35, padB = 40;
  const drawW = W - padL - padR, drawH = H - padT - padB;
  const sc = Math.min(drawW / w, drawH / h) || 0.05;
  const rw = w * sc, rh = h * sc;
  const ox = padL + (drawW - rw) / 2, oy = padT + (drawH - rh) / 2;
  const verts = [], horiz = [];
  for (let i = 1; i <= nV; i++) verts.push(ox + (rw * i) / (nV + 1));
  for (let i = 1; i <= nH; i++) horiz.push(oy + (rh * i) / (nH + 1));
  return (
    <Wrap>
      {verts.map((vx, i) => <line key={`v${i}`} x1={vx} y1={oy} x2={vx} y2={oy + rh} stroke={STROKE} strokeWidth="1.5" />)}
      {horiz.map((hy, i) => <line key={`h${i}`} x1={ox} y1={hy} x2={ox + rw} y2={hy} stroke={STROKE} strokeWidth="1.5" />)}
      <rect x={ox} y={oy} width={rw} height={rh} fill="none" stroke={STROKE} strokeWidth="3" />
      <text x={ox + rw / 2} y={oy - 10} textAnchor="middle" fontSize="10" fill="#475569">{w}mm</text>
      <text x={ox - 12} y={oy + rh / 2} textAnchor="middle" fontSize="10" fill="#475569" transform={`rotate(-90 ${ox - 12} ${oy + rh / 2})`}>{h}mm</text>
      <text x={ox + rw / 2} y={oy + rh + 22} textAnchor="middle" fontSize="10" fill={ACCENT}>{spec.total_member_lm}m member · ~{spec.weight_kg}kg</text>
    </Wrap>
  );
}

function Glass({ spec }) {
  const nPan = spec.num_panels || 0;
  const padL = 50, padR = 30;
  const oxL = padL, oxR = W - padR, topY = 55, botY = 245;
  const span = oxR - oxL;
  const pw = nPan > 0 ? span / nPan : span;
  const rects = [];
  for (let i = 0; i < nPan; i++) rects.push(oxL + i * pw);
  return (
    <Wrap>
      <line x1={oxL - 10} y1={botY} x2={oxR + 6} y2={botY} stroke={MUTED} strokeWidth="1.5" />
      {rects.map((px) => (
        <g key={px}>
          <rect x={px + 4} y={topY} width={pw - 8} height={botY - topY} fill={GLASS} stroke={ACCENT} strokeWidth="1.5" />
          <rect x={px + 10} y={botY - 6} width="8" height="12" fill={STROKE} />
          <rect x={px + pw - 18} y={botY - 6} width="8" height="12" fill={STROKE} />
        </g>
      ))}
      <text x={padL - 14} y={(topY + botY) / 2} textAnchor="middle" fontSize="10" fill="#475569" transform={`rotate(-90 ${padL - 14} ${(topY + botY) / 2})`}>{spec.height_mm}mm</text>
      <text x={(oxL + oxR) / 2} y={botY + 20} textAnchor="middle" fontSize="10" fill="#475569">{spec.length_m}m · {nPan} panels · {spec.glass_m2}m²</text>
    </Wrap>
  );
}

function Gate({ spec }) {
  const nL = spec.num_leaves || 1;
  const leafW = spec.leaf_width_mm || 1200;
  const leafH = spec.leaf_height_mm || 1500;
  const style = spec.gate_style || "swing_single";
  const infill = spec.infill_type || "bar";
  const nPieces = spec.num_infill_pieces || 0;
  const padL = 55, padR = 35, padT = 30, padB = 40;
  const drawW = W - padL - padR, drawH = H - padT - padB;
  const totalW = leafW * nL;
  const sc = Math.min(drawW / totalW, drawH / leafH) || 0.05;
  const rw = totalW * sc, rh = leafH * sc;
  const ox = padL + (drawW - rw) / 2, oy = padT + (drawH - rh) / 2;
  const leafPixel = rw / nL;

  const drawn = Math.min(nPieces, 60);
  const barsForLeaf = (leafOx) => {
    const arr = [];
    if (infill === "bar" && drawn > 0) {
      for (let i = 1; i <= drawn; i++) arr.push(leafOx + (leafPixel * i) / (drawn + 1));
    }
    return arr;
  };
  const slatsForLeaf = (leafOx) => {
    const arr = [];
    const s = Math.min(nPieces || 0, 30);
    if (infill === "slat" && s > 0) {
      for (let i = 1; i <= s; i++) arr.push(oy + (rh * i) / (s + 1));
    }
    return arr.map((sy) => [leafOx + 2, sy, leafPixel - 4, sy]);
  };

  const isSliding = style === "sliding" || style === "cantilever";

  return (
    <Wrap>
      {/* ground line */}
      <line x1={ox - 15} y1={oy + rh} x2={ox + rw + 40} y2={oy + rh} stroke={MUTED} strokeWidth="1.5" />
      {/* track for sliding */}
      {isSliding && (
        <line x1={ox - 10} y1={oy + rh + 4} x2={ox + rw + rw + 40} y2={oy + rh + 4}
          stroke={ACCENT} strokeWidth="2" strokeDasharray="6 3" />
      )}
      {/* leaves */}
      {Array.from({ length: nL }).map((_, i) => {
        const leafOx = ox + i * leafPixel;
        // Leaf frame
        return (
          <g key={`leaf-${i}`}>
            <rect x={leafOx + 1} y={oy} width={leafPixel - 2} height={rh} fill="none" stroke={STROKE} strokeWidth="3" />
            {/* mid-brace */}
            <line x1={leafOx + 1} y1={oy + rh / 2} x2={leafOx + leafPixel - 1} y2={oy + rh / 2} stroke={STROKE} strokeWidth="1.5" />
            {/* infill */}
            {infill === "bar" && barsForLeaf(leafOx).map((bx, j) => (
              <line key={`b${i}-${j}`} x1={bx} y1={oy + 4} x2={bx} y2={oy + rh - 4} stroke={STROKE} strokeWidth="1" />
            ))}
            {infill === "slat" && slatsForLeaf(leafOx).map(([x1, y1, x2, y2], j) => (
              <line key={`s${i}-${j}`} x1={leafOx + 2 + 2} y1={y1} x2={leafOx + leafPixel - 4} y2={y1} stroke={STROKE} strokeWidth="1.4" />
            ))}
            {infill === "mesh" && (
              <g>
                {[0.2, 0.4, 0.6, 0.8].map((r) => (
                  <line key={`mh${i}-${r}`} x1={leafOx + 4} y1={oy + rh * r} x2={leafOx + leafPixel - 4} y2={oy + rh * r} stroke={MUTED} strokeWidth="1" strokeDasharray="3 3" />
                ))}
                {[0.25, 0.5, 0.75].map((r) => (
                  <line key={`mv${i}-${r}`} x1={leafOx + leafPixel * r} y1={oy + 4} x2={leafOx + leafPixel * r} y2={oy + rh - 4} stroke={MUTED} strokeWidth="1" strokeDasharray="3 3" />
                ))}
              </g>
            )}
            {infill === "sheet" && (
              <rect x={leafOx + 4} y={oy + 4} width={leafPixel - 8} height={rh - 8} fill="#e2e8f0" stroke={MUTED} strokeWidth="1" />
            )}
            {infill === "laser" && (
              <g fill={STROKE} opacity="0.85">
                {Array.from({ length: 5 }).map((_, r) => Array.from({ length: 3 }).map((__, c) => (
                  <circle key={`l${i}-${r}-${c}`} cx={leafOx + 10 + c * ((leafPixel - 20) / 2)} cy={oy + 15 + r * ((rh - 30) / 4)} r="2.2" />
                )))}
              </g>
            )}
            {/* hinges (swing) or wheels (sliding) */}
            {!isSliding && (
              <g fill={ACCENT}>
                <rect x={i === 0 ? leafOx - 3 : leafOx + leafPixel - 4} y={oy + 12} width="7" height="10" />
                <rect x={i === 0 ? leafOx - 3 : leafOx + leafPixel - 4} y={oy + rh - 26} width="7" height="10" />
                {spec.hinges_needed >= 3 * nL && <rect x={i === 0 ? leafOx - 3 : leafOx + leafPixel - 4} y={oy + rh / 2 - 5} width="7" height="10" />}
              </g>
            )}
            {isSliding && i === nL - 1 && (
              <g fill={ACCENT}>
                <circle cx={leafOx + leafPixel * 0.25} cy={oy + rh + 4} r="4" />
                <circle cx={leafOx + leafPixel * 0.75} cy={oy + rh + 4} r="4" />
              </g>
            )}
          </g>
        );
      })}
      {/* latch marker on primary leaf inside edge */}
      <rect x={ox + leafPixel - 5} y={oy + rh / 2 - 3} width="4" height="10" fill="#dc2626" />

      {/* labels */}
      <text x={ox + rw / 2} y={oy - 10} textAnchor="middle" fontSize="10" fill="#475569">{leafW}mm{nL > 1 ? ` × ${nL} leaves` : ""}</text>
      <text x={ox - 12} y={oy + rh / 2} textAnchor="middle" fontSize="10" fill="#475569" transform={`rotate(-90 ${ox - 12} ${oy + rh / 2})`}>{leafH}mm</text>
      <text x={ox + rw / 2} y={oy + rh + 22} textAnchor="middle" fontSize="10" fill={ACCENT}>{style.replace("_", " ")} · {infill} infill{spec.has_motor ? " · motorised" : ""}</text>
    </Wrap>
  );
}

export default function FabricationBlueprint({ fabType, spec }) {
  if (!spec) return null;
  if (fabType === "staircase") return <Staircase spec={spec} />;
  if (fabType === "balustrade") return <Balustrade spec={spec} />;
  if (fabType === "steel_frame") return <Frame spec={spec} />;
  if (fabType === "gate") return <Gate spec={spec} />;
  if (fabType === "glass") return <Glass spec={spec} />;
  return null;
}
